import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, Tooltip, Typography
} from '@mui/material';
import { apiError } from '../../Documents/format';
import { EVENT_RESULT_COLORS, EVENT_RESULT_LABELS, EVENT_TYPE_LABELS, TRIGGER_LABELS } from '../constants';
import { isCancelled, timesheetApi } from '../timesheetApi';
import { EventsPage } from '../types';
import { fmtMoment } from '../utils';

interface Props {
  /** Меняется, когда запуск завершился или операция выполнена: журнал перечитывается */
  refreshKey: string;
}

const BORDER = '#e2e8f0';
const HEAD_SX = { fontWeight: 700, fontSize: 12, color: '#475569', bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap' } as const;

/**
 * Журнал операций с табелями: что произошло, кто (оператор или расписание), зачем (причина), когда и чем закончилось.
 * Те же записи дублируются в общем журнале действий.
 */
const OperationLog: React.FC<Props> = ({ refreshKey }) => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<EventsPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    timesheetApi.events(page, controller.signal)
      .then(res => { setData(res); setError(null); })
      .catch(err => { if (!isCancelled(err)) setError(apiError(err, 'Не удалось загрузить журнал операций')); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [page, refreshKey, reloadToken]);

  const retry = useCallback(() => setReloadToken(t => t + 1), []);

  if (error && !data) {
    return <Alert severity="error" action={<Button color="inherit" size="small" onClick={retry}>Повторить</Button>}>{error}</Alert>;
  }

  const events = data ? data.events : [];
  const pagination = data ? data.pagination : { total: 0, page: 1, limit: 20 };

  return (
    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ height: 3 }}>{loading && <LinearProgress sx={{ height: 3 }} />}</Box>
      <TableContainer sx={{ maxHeight: 420 }}>
        <Table size="small" stickyHeader sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={HEAD_SX}>Когда</TableCell>
              <TableCell sx={HEAD_SX}>Кто</TableCell>
              <TableCell sx={HEAD_SX}>Что</TableCell>
              <TableCell sx={HEAD_SX}>Зачем</TableCell>
              <TableCell sx={HEAD_SX}>Итог</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map(event => (
              <TableRow key={event.id} hover sx={{ '& td': { borderBottom: '1px solid #f1f5f9', py: 0.9, fontSize: 13, verticalAlign: 'top' } }}>
                <TableCell sx={{ whiteSpace: 'nowrap', color: '#475569' }}>{fmtMoment(event.createdAt)}</TableCell>
                <TableCell sx={{ color: '#475569' }}>
                  <Typography variant="body2" sx={{ fontSize: 13 }}>{event.actorName || '-'}</Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>{TRIGGER_LABELS[event.triggerType] || event.triggerType}</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 220 }}>
                  <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 600 }}>
                    {EVENT_TYPE_LABELS[event.type] || event.type}{event.year ? `, ${event.year}` : ''}
                  </Typography>
                  {event.message && <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{event.message}</Typography>}
                </TableCell>
                <TableCell sx={{ color: '#475569', minWidth: 160, maxWidth: 260 }}>
                  {event.reason ? (
                    <Tooltip title={event.reason} placement="top-start">
                      <Typography
                        variant="body2"
                        sx={{ fontSize: 13, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
                      >
                        {event.reason}
                      </Typography>
                    </Tooltip>
                  ) : '-'}
                </TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" color={EVENT_RESULT_COLORS[event.result] || 'default'} label={EVENT_RESULT_LABELS[event.result] || event.result} />
                </TableCell>
              </TableRow>
            ))}
            {!loading && events.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">Операций ещё не было</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination.total > pagination.limit && (
        <TablePagination
          component="div" count={pagination.total} page={Math.max(0, pagination.page - 1)} rowsPerPage={pagination.limit}
          rowsPerPageOptions={[]} onPageChange={(_, p) => setPage(p + 1)}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
          sx={{ borderTop: `1px solid ${BORDER}` }}
        />
      )}
    </Box>
  );
};

export default React.memo(OperationLog);
